import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import FocusedDialog from '../ui/FocusedDialog';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { X, Upload, AlertCircle, ChevronDown } from 'lucide-react';
import { PublishBasketRequest } from '../../types/marketplace';
import { MyPublisherGroup } from '../../types/publisherGroups';
import marketplaceService from '../../services/marketplaceService';
import publisherGroupsService from '../../services/publisherGroupsService';
import { isWhiteLabel } from '../../config/tenantConfig';

interface PublishBasketDialogProps {
  basketId: string;
  basketName: string;
  onClose: () => void;
  onSuccess: () => void;
}

const CATEGORIES = ['CONSERVATIVE', 'AGGRESSIVE', 'HEDGED', 'INCOME', 'MOMENTUM'];
const RISK_LEVELS = ['LOW', 'MEDIUM', 'HIGH'];

const PublishBasketDialog: React.FC<PublishBasketDialogProps> = ({ basketId, basketName, onClose, onSuccess }) => {
  const wl = isWhiteLabel();
  const focusRing = wl ? 'focus:ring-zebu-500 focus:border-zebu-500' : 'focus:ring-blue-500 focus:border-blue-500';
  const accentText = wl ? 'text-zebu-600' : 'text-blue-600';
  const [formData, setFormData] = useState({
    title: basketName,
    description: '',
    category: '',
    risk_level: '',
    base_lot_size: 1,
    min_lot_multiplier: 1,
    max_lot_multiplier: 10,
    capital_required: 500000,
    tags: '',
  });
  // marketplace-publisher-groups Phase 8: multi-select group picker fed by
  // GET /admin/marketplace/my-publisher-groups (caller's PUBLISHER memberships).
  // At least one group required.
  const [availableGroups, setAvailableGroups] = useState<MyPublisherGroup[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [groupsLoaded, setGroupsLoaded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const groups = await publisherGroupsService.listMyPublisherGroups();
        if (cancelled) return;
        setAvailableGroups(groups);
        // No default selection — the user must explicitly pick at least one
        // group. The pre-Phase-3 auto-select-public was removed after a
        // real publisher accidentally shipped a basket to both `public` and
        // their custom group because they didn't realize public was already
        // checked when they added the custom group.
      } catch (e: any) {
        if (!cancelled) setError(e.message || 'Failed to load publisher groups');
      } finally {
        if (!cancelled) setGroupsLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleGroup = (slug: string) => {
    setSelectedGroups((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug].sort(),
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }
    if (!formData.description.trim()) {
      setError('Description is required');
      return;
    }
    if (!formData.category) {
      setError('Category is required');
      return;
    }
    if (!formData.risk_level) {
      setError('Risk level is required');
      return;
    }
    if (selectedGroups.length === 0) {
      setError('Select at least one publisher group');
      return;
    }

    setIsSubmitting(true);

    try {
      const request: PublishBasketRequest = {
        basket_id: basketId,
        title: formData.title.trim(),
        description: formData.description.trim(),
        category: formData.category,
        risk_level: formData.risk_level,
        base_lot_size: formData.base_lot_size,
        min_lot_multiplier: formData.min_lot_multiplier,
        max_lot_multiplier: formData.max_lot_multiplier,
        capital_required: formData.capital_required,
        tags: formData.tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
        published_to_groups: selectedGroups,
      };

      await marketplaceService.publishBasket(request);
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to publish basket');
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const update = (field: string, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <FocusedDialog
        ariaLabel="Publish to marketplace"
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto"
      >
        <Card className="border-0 shadow-none">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Upload className={`h-5 w-5 ${accentText}`} />
                Publish to Marketplace
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClose}
                disabled={isSubmitting}
                className="h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Make this basket available for subscribers
            </p>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-red-800 dark:text-red-200">{error}</span>
                  </div>
                </div>
              )}

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Title *
                </label>
                <Input
                  value={formData.title}
                  onChange={(e) => update('title', e.target.value)}
                  placeholder="Strategy title for marketplace"
                  disabled={isSubmitting}
                  className="w-full"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Description *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => update('description', e.target.value)}
                  placeholder="Describe the strategy approach, risk profile, and expected behavior..."
                  disabled={isSubmitting}
                  rows={3}
                  className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm resize-none focus:ring-2 ${focusRing} placeholder-gray-500 dark:placeholder-gray-400`}
                />
              </div>

              {/* Category + Risk Level */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    Category *
                  </label>
                  <div className="relative">
                    <select
                      value={formData.category}
                      onChange={(e) => update('category', e.target.value)}
                      disabled={isSubmitting}
                      className={`w-full h-10 px-3 pr-8 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white appearance-none focus:ring-2 ${focusRing}`}
                    >
                      <option value="">Select</option>
                      {CATEGORIES.map((c) => (
                        <option key={c} value={c}>{c.charAt(0) + c.slice(1).toLowerCase()}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2.5 top-2.5 h-5 w-5 text-gray-400 pointer-events-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    Risk Level *
                  </label>
                  <div className="relative">
                    <select
                      value={formData.risk_level}
                      onChange={(e) => update('risk_level', e.target.value)}
                      disabled={isSubmitting}
                      className={`w-full h-10 px-3 pr-8 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white appearance-none focus:ring-2 ${focusRing}`}
                    >
                      <option value="">Select</option>
                      {RISK_LEVELS.map((r) => (
                        <option key={r} value={r}>{r.charAt(0) + r.slice(1).toLowerCase()}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2.5 top-2.5 h-5 w-5 text-gray-400 pointer-events-none" />
                  </div>
                </div>
              </div>

              {/* Publisher groups (marketplace-publisher-groups Phase 8) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Publish to groups <span className="text-red-500">*</span>
                </label>
                {!groupsLoaded ? (
                  <div className="text-xs text-gray-500 dark:text-gray-400">Loading…</div>
                ) : availableGroups.length === 0 ? (
                  <div className="text-xs text-yellow-700 dark:text-yellow-300 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded p-2">
                    You are not a publisher of any group. Ask a SuperAdmin to grant you publisher membership.
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {availableGroups.map((g) => {
                      const active = selectedGroups.includes(g.group_id);
                      return (
                        <button
                          key={g.group_id}
                          type="button"
                          onClick={() => toggleGroup(g.group_id)}
                          disabled={isSubmitting}
                          title={g.description || g.display_name}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                            active
                              ? 'bg-blue-600 text-white border-blue-600 dark:bg-blue-500 dark:border-blue-500'
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600'
                          }`}
                        >
                          {g.display_name}
                        </button>
                      );
                    })}
                  </div>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Select one or more groups. Listings tagged <code>public</code> appear on the public marketplace; others surface to entitled managed users only.
                </p>
              </div>

              {/* Lot Configuration */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    Base Lots
                  </label>
                  <Input
                    type="number"
                    value={formData.base_lot_size}
                    onChange={(e) => update('base_lot_size', parseInt(e.target.value) || 1)}
                    min={1}
                    disabled={isSubmitting}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    Min Mult.
                  </label>
                  <Input
                    type="number"
                    value={formData.min_lot_multiplier}
                    onChange={(e) => update('min_lot_multiplier', parseInt(e.target.value) || 1)}
                    min={1}
                    disabled={isSubmitting}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    Max Mult.
                  </label>
                  <Input
                    type="number"
                    value={formData.max_lot_multiplier}
                    onChange={(e) => update('max_lot_multiplier', parseInt(e.target.value) || 1)}
                    min={formData.min_lot_multiplier}
                    disabled={isSubmitting}
                    className="w-full"
                  />
                </div>
              </div>

              {/* Capital Required */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Capital Required (₹)
                </label>
                <Input
                  type="number"
                  value={formData.capital_required}
                  onChange={(e) => update('capital_required', parseInt(e.target.value) || 500000)}
                  min={1}
                  disabled={isSubmitting}
                  className="w-full"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Minimum capital needed to run this strategy
                </p>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Tags
                </label>
                <Input
                  value={formData.tags}
                  onChange={(e) => update('tags', e.target.value)}
                  placeholder="nifty, bank-nifty, intraday"
                  disabled={isSubmitting}
                  className="w-full"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Comma-separated tags for discoverability
                </p>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={
                    isSubmitting
                    || !formData.title.trim()
                    || !formData.category
                    || !formData.risk_level
                    || selectedGroups.length === 0
                  }
                  className="flex-1"
                  leftIcon={isSubmitting ? undefined : <Upload className="h-4 w-4" />}
                >
                  {isSubmitting ? 'Publishing...' : 'Publish'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </FocusedDialog>
    </div>
  );
};

export default PublishBasketDialog;
